import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
  Divider,
  Paper,
  Card,
  CardContent,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close,
  AttachMoney,
  Message,
  Send,
  Person,
  LocalOffer,
  TrendingDown,
  CheckCircle,
  FlashOn,
  Handshake,
  Favorite,
  Info,
  Warning,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { OffersService } from '../../services/offersService';
import { MessageService } from '../../services/messageService';
import { supabase } from '../../lib/supabase';

interface NewOfferPopupProps {
  open: boolean;
  onClose: () => void;
  product: {
    id: string;
    name: string;
    price: number;
    image: string;
    sellerId?: string;
    seller: {
      name: string;
      avatar?: string;
    };
  };
  onSuccess?: () => void;
}

const NewOfferPopup: React.FC<NewOfferPopupProps> = ({
  open,
  onClose,
  product,
  onSuccess,
}) => {
  const theme = useTheme();
  const [offerPrice, setOfferPrice] = useState<number>(product.price * 0.9); // Default to 90% of listed price
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<{ price?: string; message?: string }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [existingOffer, setExistingOffer] = useState<any | null>(null);
  const [showExistingOfferDialog, setShowExistingOfferDialog] = useState(false);
  const [checkingExistingOffer, setCheckingExistingOffer] = useState(false);

  // Check for existing offers when popup opens
  useEffect(() => {
    if (open) {
      checkExistingOffer();
      setOfferPrice(product.price * 0.9);
      setMessage('');
      setErrors({});
      setError(null);
      setSuccess(false);
    }
  }, [open, product.id, product.price]);

  const checkExistingOffer = async () => {
    setCheckingExistingOffer(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setCheckingExistingOffer(false);
        return;
      }

      // Check if user has an existing offer for this product
      const { data: existingOffers, error: offersError } = await supabase
        .from('offers')
        .select('*')
        .eq('product_id', product.id)
        .eq('buyer_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);

      if (offersError) {
        console.error('Error checking existing offers:', offersError);
        setCheckingExistingOffer(false);
        return;
      }

      if (existingOffers && existingOffers.length > 0) {
        const mostRecentOffer = existingOffers[0];
        setExistingOffer(mostRecentOffer);
        setShowExistingOfferDialog(true);
      }
    } catch (err) {
      console.error('Error checking existing offer:', err);
    } finally {
      setCheckingExistingOffer(false);
    }
  };

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value) || 0;
    setOfferPrice(value);
    
    if (errors.price) {
      setErrors(prev => ({ ...prev, price: undefined }));
    }
    setError(null);
  };

  const handleMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value);
    
    if (errors.message) {
      setErrors(prev => ({ ...prev, message: undefined }));
    }
    setError(null);
  };

  const getMessageTemplates = () => {
    const savings = product.price - offerPrice;
    const savingsPercentage = product.price > 0 ? ((savings / product.price) * 100).toFixed(0) : '0';
    
    return [
      {
        id: 'friendly',
        label: 'Friendly',
        icon: <Favorite fontSize="small" />,
        message: `Hi! I'm very interested in ${product.name}. I'm offering $${offerPrice.toLocaleString()} (${savingsPercentage}% off the listed price). Would you be open to negotiating? I'm ready to move forward quickly if we can agree on a price. Thanks!`
      },
      {
        id: 'quick',
        label: 'Quick Purchase',
        icon: <FlashOn fontSize="small" />,
        message: `Hello! I'm prepared to purchase ${product.name} right away for $${offerPrice.toLocaleString()}. I can pay immediately if you accept this offer. Looking forward to your response!`
      },
      {
        id: 'professional',
        label: 'Professional',
        icon: <Handshake fontSize="small" />,
        message: `Good day! I'm interested in purchasing ${product.name} and would like to make an offer of $${offerPrice.toLocaleString()}. ${savings > 0 ? `This represents a ${savingsPercentage}% discount from your listed price of $${product.price.toLocaleString()}.` : 'This matches your listed price.'} Please let me know if this works for you. Thank you!`
      },
      {
        id: 'casual',
        label: 'Casual',
        icon: <Message fontSize="small" />,
        message: `Hey! Love the ${product.name}. Could we do $${offerPrice.toLocaleString()}? ${savings > 0 ? `That's about ${savingsPercentage}% off your asking price.` : 'That matches your asking price.'} Let me know what you think!`
      },
      {
        id: 'negotiable',
        label: 'Open to Negotiate',
        icon: <AttachMoney fontSize="small" />,
        message: `Hi there! I'm interested in ${product.name} and would like to offer $${offerPrice.toLocaleString()}. I'm open to discussing the price if you'd like to counter. What do you think?`
      }
    ];
  };

  const handleTemplateClick = (templateMessage: string) => {
    setMessage(templateMessage);
    if (errors.message) {
      setErrors(prev => ({ ...prev, message: undefined }));
    }
    setError(null);
  };

  const validateForm = () => {
    const newErrors: { price?: string; message?: string } = {};

    if (offerPrice <= 0) {
      newErrors.price = 'Please enter a valid offer price';
    }

    if (offerPrice > product.price) {
      newErrors.price = 'Your offer cannot be higher than the listed price';
    }

    if (!message.trim()) {
      newErrors.message = 'Please include a message with your offer';
    }

    if (message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      let sellerId = product.sellerId;

      // If sellerId is not provided, fetch it from the product
      if (!sellerId) {
        console.log('NewOfferPopup - sellerId not provided, fetching from product...');
        try {
          const { data: productData, error: productError } = await supabase
            .from('products')
            .select('seller_id')
            .eq('id', product.id)
            .single();

          if (productError || !productData) {
            throw new Error('Failed to fetch seller information. Please try again.');
          }

          sellerId = productData.seller_id;
          console.log('NewOfferPopup - Fetched sellerId:', sellerId);
        } catch (fetchError: any) {
          console.error('NewOfferPopup - Error fetching sellerId:', fetchError);
          setError(fetchError.message || 'Failed to fetch seller information. Please refresh the page and try again.');
          setLoading(false);
          return;
        }
      }

      if (!sellerId) {
        setError('Seller information is missing. Please refresh the page and try again.');
        setLoading(false);
        return;
      }

      console.log('NewOfferPopup - Creating offer with data:', {
        product_id: product.id,
        seller_id: sellerId,
        offer_price: offerPrice,
        offer_message: message.trim(),
      });

      // Step 1: Create the offer
      const createdOffer = await OffersService.createOffer({
        product_id: product.id,
        seller_id: sellerId,
        offer_price: offerPrice,
        offer_message: message.trim(),
      });

      console.log('NewOfferPopup - Offer created successfully:', createdOffer);

      // Step 2: Create or get conversation with the seller, linking it to the offer
      console.log('NewOfferPopup - Creating conversation with seller and linking to offer...');
      const conversation = await MessageService.createConversationWithProduct(
        sellerId,
        product.id,
        `Offer for ${product.name}`,
        createdOffer.id  // Pass the offer_id to link the conversation to the offer
      );

      console.log('NewOfferPopup - Conversation created/retrieved with offer_id:', conversation);

      // Step 3: Send offer message in the conversation
      console.log('NewOfferPopup - Sending offer message...');
      await MessageService.sendOfferMessage(
        sellerId,
        product.id,
        createdOffer.id,
        offerPrice,
        message.trim(),
        `Offer for ${product.name}`,
        conversation.id
      );

      console.log('NewOfferPopup - Offer message sent successfully');

      setSuccess(true);
      
      // Wait a moment to show success message, then close
      setTimeout(() => {
        setSuccess(false);
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error('NewOfferPopup - Error creating offer:', err);
      setError(err.message || 'Failed to create offer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return; // Prevent closing while loading
    setOfferPrice(product.price * 0.9);
    setMessage('');
    setErrors({});
    setError(null);
    setSuccess(false);
    onClose();
  };

  const savings = product.price - offerPrice;
  const savingsPercentage = product.price > 0 ? (savings / product.price) * 100 : 0;

  const handleContinueNewOffer = () => {
    setShowExistingOfferDialog(false);
    setExistingOffer(null);
  };

  const handleQuit = () => {
    setShowExistingOfferDialog(false);
    setExistingOffer(null);
    handleClose();
  };

  return (
    <>
      {/* Existing Offer Warning Dialog */}
      <Dialog
        open={showExistingOfferDialog && !!existingOffer}
        onClose={handleQuit}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          pb: 2,
          background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
          color: 'white',
        }}>
          <Box sx={{ 
            bgcolor: 'rgba(255,255,255,0.2)', 
            borderRadius: 2, 
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Warning sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Existing Offer Found
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              You've already made an offer for this product
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 3 }}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              mb: 2,
              borderRadius: 2,
              bgcolor: 'rgba(255, 152, 0, 0.1)',
              border: '1px solid rgba(255, 152, 0, 0.3)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Info sx={{ fontSize: 32, color: 'warning.main' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" fontWeight="bold" gutterBottom>
                  Your Previous Offer
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Status: <strong style={{ textTransform: 'capitalize' }}>{existingOffer?.status || 'pending'}</strong>
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Offer Price:
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                ${existingOffer?.offer_price?.toLocaleString() || '0.00'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Listed Price:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                ${product.price.toLocaleString()}
              </Typography>
            </Box>

            {existingOffer?.offer_message && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Your Message:
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    bgcolor: 'white',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    "{existingOffer.offer_message}"
                  </Typography>
                </Paper>
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Created: {existingOffer?.created_at 
                  ? new Date(existingOffer.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : 'Unknown'}
              </Typography>
            </Box>
          </Card>

          <Alert severity="info" sx={{ borderRadius: 2 }}>
            <Typography variant="body2">
              Would you like to make a <strong>new offer</strong> or would you prefer to <strong>quit</strong> and keep your existing offer?
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleQuit}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              py: 1.5,
              px: 4,
              borderRadius: 2,
              borderColor: '#ddd',
              '&:hover': {
                borderColor: '#999',
                bgcolor: 'rgba(0,0,0,0.04)',
              }
            }}
          >
            Quit
          </Button>
          <Button
            variant="contained"
            onClick={handleContinueNewOffer}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              textTransform: 'none',
              fontWeight: 'bold',
              py: 1.5,
              px: 4,
              borderRadius: 999,
              boxShadow: `0 14px 34px ${alpha(theme.palette.primary.main, 0.26)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                boxShadow: `0 18px 44px ${alpha(theme.palette.primary.main, 0.34)}`,
                transform: 'translateY(-1px)',
              }
            }}
          >
            Make New Offer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Offer Popup */}
      <Dialog
        open={open && !showExistingOfferDialog}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            background: 'linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%)',
          }
        }}
      >
      <AnimatePresence>
        {success ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            style={{ padding: '3rem', textAlign: 'center' }}
          >
            <CheckCircle sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Offer Sent Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your offer has been sent to the seller. They will be notified and can respond to your offer.
            </Typography>
          </motion.div>
        ) : (
          <>
            <DialogTitle sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              pb: 2,
              pt: 3,
              px: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              color: 'white',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  borderRadius: 2, 
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <LocalOffer sx={{ fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    Make an Offer
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                    Negotiate the price with the seller
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleClose}
                size="small"
                disabled={loading}
                sx={{
                  color: 'white',
                  bgcolor: alpha(theme.palette.common.white, 0.18),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.24),
                  },
                  '&:disabled': {
                    opacity: 0.5,
                  }
                }}
              >
                <Close />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 3, py: 3 }}>
              {/* Loading indicator while checking for existing offers */}
              {checkingExistingOffer ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    Checking for existing offers...
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Product Info Card */}
                  <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: 2,
                  bgcolor: 'white',
                  border: '1px solid #e0e0e0',
                  display: 'flex',
                  gap: 2.5,
                  alignItems: 'center',
                }}
              >
                <Box
                  component="img"
                  src={product.image}
                  alt={product.name}
                  sx={{
                    width: 100,
                    height: 100,
                    objectFit: 'cover',
                    borderRadius: 2,
                    border: '2px solid #f0f0f0',
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                    {product.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                    <Chip
                      label={`$${product.price.toLocaleString()}`}
                      color="primary"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        height: 32,
                      }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {product.seller.name}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {/* Offer Form */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  Your Offer Details
                </Typography>
                
                <TextField
                  fullWidth
                  label="Your Offer Price"
                  type="number"
                  value={offerPrice || ''}
                  onChange={handlePriceChange}
                  error={!!errors.price}
                  helperText={errors.price}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoney sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                {offerPrice > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 2,
                        bgcolor: savings > 0 
                          ? 'rgba(76, 175, 80, 0.1)' 
                          : savings < 0
                          ? 'rgba(255, 152, 0, 0.1)'
                          : 'rgba(33, 150, 243, 0.1)',
                        border: `1px solid ${
                          savings > 0 
                            ? 'rgba(76, 175, 80, 0.3)' 
                            : savings < 0
                            ? 'rgba(255, 152, 0, 0.3)'
                            : 'rgba(33, 150, 243, 0.3)'
                        }`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <TrendingDown sx={{ 
                        color: savings > 0 ? 'success.main' : 'primary.main',
                        fontSize: 32 
                      }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {savings > 0 ? (
                            <>
                              You're offering <strong>${savings.toFixed(2)} less</strong> than the listed price
                            </>
                          ) : savings < 0 ? (
                            <>
                              You're offering <strong>${Math.abs(savings).toFixed(2)} more</strong> than the listed price
                            </>
                          ) : (
                            "You're offering the exact listed price"
                          )}
                        </Typography>
                        {savings > 0 && (
                          <Typography variant="h6" fontWeight="bold" color="success.main">
                            {savingsPercentage.toFixed(1)}% discount
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  </motion.div>
                )}

                {/* Message Templates */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
                    Quick message templates:
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1,
                  }}>
                    {getMessageTemplates().map((template) => (
                      <Button
                        key={template.id}
                        size="small"
                        variant="outlined"
                        startIcon={template.icon}
                        onClick={() => handleTemplateClick(template.message)}
                        disabled={offerPrice <= 0}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          borderRadius: 2,
                          borderColor: '#ddd',
                          color: 'text.primary',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'primary.50',
                            color: 'primary.main',
                          },
                          '&:disabled': {
                            opacity: 0.5,
                          }
                        }}
                      >
                        {template.label}
                      </Button>
                    ))}
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  label="Message to Seller"
                  multiline
                  rows={4}
                  value={message}
                  onChange={handleMessageChange}
                  error={!!errors.message}
                  helperText={errors.message || "Explain why you're interested and any additional details"}
                  placeholder="Hi! I'm interested in this product because..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                        <Message sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Info Alert */}
              <Alert 
                severity="info" 
                icon={<Message />}
                sx={{ 
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    color: 'primary.main'
                  }
                }}
              >
                <Typography variant="body2">
                  <strong>How it works:</strong> Your offer will be sent to the seller as a message. 
                  They can accept, decline, or counter your offer. You'll be notified when they respond.
                </Typography>
              </Alert>

              <DialogActions sx={{ 
                px: 3, 
                pb: 3, 
                gap: 2,
                borderTop: '1px solid #e0e0e0',
                pt: 2,
              }}>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  disabled={loading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'bold',
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    borderColor: '#ddd',
                    '&:hover': {
                      borderColor: '#999',
                      bgcolor: 'rgba(0,0,0,0.04)',
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading || offerPrice <= 0 || !message.trim()}
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Send />}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    py: 1.5,
                    px: 4,
                    borderRadius: 999,
                    boxShadow: `0 14px 34px ${alpha(theme.palette.primary.main, 0.32)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                      boxShadow: `0 18px 44px ${alpha(theme.palette.primary.main, 0.40)}`,
                      transform: 'translateY(-1px)',
                    },
                    '&:disabled': {
                      background: 'rgba(0,0,0,0.12)',
                      color: 'rgba(0,0,0,0.26)',
                      boxShadow: 'none',
                      transform: 'none',
                    }
                  }}
                >
                  {loading ? 'Sending Offer...' : 'Send Offer'}
                </Button>
              </DialogActions>
                </>
              )}
            </DialogContent>
          </>
        )}
      </AnimatePresence>
      </Dialog>
    </>
  );
};

export default NewOfferPopup;

